import { useEffect, useMemo, useState } from 'react';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import { sortBy } from 'lodash-es';

import { cmApi } from '@src/api';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import type { TableComponentRequestDto } from '@src/api';

type ReturnType = {
  statusItems: {
    id: string;
    name: string;
  }[];
  yearItems: {
    id: string;
    name: string;
  }[];
  reportGroupItems: {
    id: string;
    name: string;
  }[];
  periodItems: {
    id: string;
    name: string;
  }[];
  packageItems: {
    id: string;
    name: string;
  }[];
};

export const useGetFilterItems = (organization?: string): ReturnType => {
  const [statusItems, setStatusItems] = useState<{ id: string; name: string }[]>([]);

  const [mainFilters, setMainFilters] = useState<
    { rep_group_code: string; year: string; period_code: string; report_package_code_cm: string }[]
  >([]);

  const { reportGroups } = reportGroupsStore;
  const { periods } = periodsStore;

  useEffect(() => {
    if (!organization) return;

    const tableInfo = {
      fields: ['status', 'status_text'],
      hardFilter: [
        [
          {
            name: 'status_schema',
            type: FILTER.EQ,
            filterIsInverted: false,
            values: ['CTRL_2'],
          },
        ],
      ],
      limit: 100,
      offset: 0,
      resource: 'ctrl.nsi_ctrl_status',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as { data: { status: string; status_text: string }[] };
      if (result && result.data && result.data.length > 0) {
        const out = result.data.map((item) => ({
          id: item.status,
          name: `${item.status} - ${item.status_text}`,
        }));
        setStatusItems(out);
      }
    });
  }, [organization]);

  useEffect(() => {
    if (!organization) return;

    const tableInfo = {
      fields: ['rep_group_code', 'year', 'period_code', 'report_package_code_cm'],
      hardFilter: [
        [
          {
            name: 'org_code',
            type: FILTER.EQ,
            filterIsInverted: false,
            values: [organization],
          },
        ],
      ],
      limit: 100,
      offset: 0,
      resource: 'v_ctrl_main',
    };

    cmApi.tableComponentV2.getTableJson(tableInfo as unknown as TableComponentRequestDto).then((response) => {
      const result = response?.data as unknown as {
        data: { rep_group_code: string; year: string; period_code: string; report_package_code_cm: string }[];
      };

      if (result && result.data && result.data.length > 0) {
        setMainFilters(result.data);
      }
    });
  }, [organization]);

  return useMemo(
    () => ({
      statusItems,
      yearItems: mainFilters.map((item) => ({
        ...item,
        id: item.year,
        name: item.year,
      })),
      reportGroupItems: mainFilters.map((item) => {
        const groupName = reportGroups?.find((el) => el.code === item.rep_group_code)?.name;

        return {
          ...item,
          id: item.rep_group_code,
          name: groupName ? `${item.rep_group_code} - ${groupName}` : item.rep_group_code,
        };
      }),
      periodItems: sortBy(mainFilters, 'period_code').map((item) => {
        const periodName = periods?.find((el) => el.code === item.period_code)?.name;

        return {
          ...item,
          id: item.period_code,
          name: periodName ? `${item.period_code} - ${periodName}` : item.period_code,
        };
      }),
      packageItems: mainFilters.map((item) => ({
        ...item,
        id: item.report_package_code_cm,
        name: item.report_package_code_cm,
      })),
    }),
    [mainFilters, periods, reportGroups, statusItems],
  );
};
